import React, { createContext, useContext, useEffect, useState } from "react";
import { authClient } from "../lib/auth-client";
import { DatabaseService } from "../services/database";
import { ReferralTracker } from "../utils/referralTracker";
import {
  ProfileCacheService,
  CachedUserProfile,
} from "../services/profileCache";
import { IdentityService } from "../services/identity";
import { UserIdentity } from "../types/user";

interface User {
  id: string;
  email: string;
  name?: string;
  subscription_plan?: string;
  // Optional profile fields
  profile_image_url?: string;
  university?: string;
  university_email?: string;
  status?: "applying" | "in_university" | "graduated";
}

interface SubscriptionData {
  plan: string;
  usageCount: number;
  canAnalyze: boolean;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  subscriptionData: SubscriptionData | null;
  subscriptionLoading: boolean;
  userOnboarded: boolean | null;
  // Enhanced profile data from cache
  cachedProfile: CachedUserProfile | null;
  identity: UserIdentity | null;
  identityLoading: boolean;
  signOut: () => Promise<void>;
  refreshSubscription: () => Promise<void>;
  refreshOnboardingStatus: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  refreshIdentity: () => Promise<void>;
  invalidateCache: () => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  subscriptionData: null,
  subscriptionLoading: false,
  userOnboarded: null,
  cachedProfile: null,
  identity: null,
  identityLoading: false,
  signOut: async () => {},
  refreshSubscription: async () => {},
  refreshOnboardingStatus: async () => {},
  refreshProfile: async () => {},
  refreshIdentity: async () => {},
  invalidateCache: () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [subscriptionData, setSubscriptionData] =
    useState<SubscriptionData | null>(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [userOnboarded, setUserOnboarded] = useState<boolean | null>(null);
  const [cachedProfile, setCachedProfile] = useState<CachedUserProfile | null>(
    null
  );
  const [identity, setIdentity] = useState<UserIdentity | null>(null);
  const [identityLoading, setIdentityLoading] = useState(false);

  // Cache key for localStorage
  const getOnboardingCacheKey = (userId: string) =>
    `onboarding_status_${userId}`;

  // Fetch onboarding status for the current user
  const fetchOnboardingStatus = async (userId: string) => {
    try {
      // First check cache
      const cacheKey = getOnboardingCacheKey(userId);
      const cachedStatus = localStorage.getItem(cacheKey);

      if (cachedStatus !== null) {
        const isOnboarded = cachedStatus === "true";
        setUserOnboarded(isOnboarded);

        // If user is not onboarded according to cache, still check DB in background
        // to handle case where they completed onboarding in another tab
        if (!isOnboarded) {
          // Check DB in background
          DatabaseService.getCurrentUser(userId)
            .then((dbUser) => {
              const actualStatus = dbUser?.onboarded || false;
              if (actualStatus !== isOnboarded) {
                // Update cache and state if status changed
                localStorage.setItem(cacheKey, actualStatus.toString());
                setUserOnboarded(actualStatus);
              }
            })
            .catch((error) => {
              console.error(
                "Background onboarding status check failed:",
                error
              );
            });
        }
        return; // Return early with cached value
      }

      // No cache - fetch from database
      const dbUser = await DatabaseService.getCurrentUser(userId);
      const status = dbUser?.onboarded || false;
      setUserOnboarded(status);

      // Cache the result
      localStorage.setItem(cacheKey, status.toString());
    } catch (error) {
      console.error("Error fetching onboarding status:", error);
      setUserOnboarded(false);
    }
  };

  // Fetch subscription data for the current user
  const fetchSubscriptionData = async (userId: string) => {
    try {
      setSubscriptionLoading(true);
      const result = await DatabaseService.checkUserCanAnalyze(userId);
      setSubscriptionData({
        plan: result.subscriptionPlan,
        usageCount: result.usageCount,
        canAnalyze: result.canAnalyze,
      });
    } catch (error) {
      console.error("Error fetching subscription data:", error);
      // Set default values on error
      setSubscriptionData({
        plan: "free",
        usageCount: 0,
        canAnalyze: true,
      });
    } finally {
      setSubscriptionLoading(false);
    }
  };

  // Public method to refresh subscription data
  const refreshSubscription = async () => {
    if (user?.id) {
      await fetchSubscriptionData(user.id);
    }
  };

  const refreshOnboardingStatus = async () => {
    if (user?.id) {
      // Clear cache and fetch fresh from database
      const cacheKey = getOnboardingCacheKey(user.id);
      localStorage.removeItem(cacheKey);
      await fetchOnboardingStatus(user.id);
    }
  };

  // Fetch and cache complete user profile data
  const fetchProfileData = async (userId: string, useCache: boolean = true) => {
    try {
      // Check cache first if requested
      if (useCache) {
        const cached = ProfileCacheService.getCachedProfile(userId);
        if (cached && !ProfileCacheService.isProfileStale(userId)) {
          setCachedProfile(cached);
          return cached;
        }
      }

      // Fetch fresh data from database
      const dbUser = await DatabaseService.getCurrentUser(userId);
      if (dbUser) {
        // Create comprehensive profile cache object
        const profileData: CachedUserProfile = {
          id: dbUser.id,
          email: dbUser.email,
          name: dbUser.name,
          profile_image_url: dbUser.profile_image_url,
          university: dbUser.university,
          university_email: dbUser.university_email,
          bio: dbUser.bio,
          age: dbUser.age,
          major: dbUser.major,
          education_level: dbUser.education_level,
          status: dbUser.status,
          subscription_plan: dbUser.subscription_plan,
          roles: dbUser.roles,
          onboarded: dbUser.onboarded,
        };

        // Cache the profile data
        ProfileCacheService.setCachedProfile(userId, profileData);
        setCachedProfile(profileData);
        return profileData;
      }
    } catch (error) {
      console.error("Error fetching profile data:", error);
    }
    return null;
  };

  // Fetch and cache user identity data
  const fetchIdentityData = async (
    userId: string,
    useCache: boolean = true
  ) => {
    try {
      setIdentityLoading(true);

      // Check cache first if requested
      if (useCache) {
        const cached = ProfileCacheService.getCachedIdentity(userId);
        if (cached && !ProfileCacheService.isIdentityStale(userId)) {
          setIdentity(cached);
          setIdentityLoading(false);
          return cached;
        }
      }

      // Fetch fresh identity data
      const userIdentity = await IdentityService.getUserIdentity(userId);

      // Cache the identity data
      ProfileCacheService.setCachedIdentity(userId, userIdentity);
      setIdentity(userIdentity);

      return userIdentity;
    } catch (error) {
      console.error("Error fetching identity data:", error);
      setIdentity(null);
    } finally {
      setIdentityLoading(false);
    }
    return null;
  };

  // Public method to refresh profile data
  const refreshProfile = async () => {
    if (user?.id) {
      await fetchProfileData(user.id, false); // Skip cache
    }
  };

  // Public method to refresh identity data
  const refreshIdentity = async () => {
    if (user?.id) {
      await fetchIdentityData(user.id, false); // Skip cache
    }
  };

  // Public method to invalidate all cached data for current user
  const invalidateCache = () => {
    if (user?.id) {
      ProfileCacheService.clearUserCache(user.id);
      setCachedProfile(null);
      setIdentity(null);

      // Refetch all data
      fetchProfileData(user.id, false);
      fetchIdentityData(user.id, false);
      fetchOnboardingStatus(user.id);
      fetchSubscriptionData(user.id);
    }
  };

  useEffect(() => {
    // Capture any referral code from URL
    ReferralTracker.captureReferralFromUrl();

    // Get initial session
    const getSession = async () => {
      try {
        const { data: session } = await authClient.getSession();
        const sessionUser = session?.user ?? null;

        // Optimized auth loading: Set user immediately, sync data in background
        if (sessionUser?.id) {
          // Set user immediately for faster UX
          setUser(sessionUser);

          // Load cached data immediately for instant UI
          const cached = ProfileCacheService.getCachedProfile(sessionUser.id);
          if (cached) {
            setCachedProfile(cached);
            if (cached.onboarded !== undefined) {
              setUserOnboarded(cached.onboarded);
            }
            if (cached.identity) {
              setIdentity(cached.identity);
            }
          }

          // Check legacy onboarding cache if no profile cache
          if (!cached) {
            const cacheKey = getOnboardingCacheKey(sessionUser.id);
            const cachedStatus = localStorage.getItem(cacheKey);
            if (cachedStatus !== null) {
              setUserOnboarded(cachedStatus === "true");
            }
          }

          // Fetch all data in background (non-blocking)
          Promise.all([
            fetchSubscriptionData(sessionUser.id),
            fetchOnboardingStatus(sessionUser.id), // Always refresh to catch updates
            fetchProfileData(sessionUser.id, true), // Use cache but refresh if stale
            fetchIdentityData(sessionUser.id, true), // Use cache but refresh if stale
          ]).catch((error) => {
            console.error("Background auth data sync failed:", error);
          });
        } else {
          setUser(null);
          setSubscriptionData(null);
          setUserOnboarded(null);
          setCachedProfile(null);
          setIdentity(null);
        }
      } catch (error) {
        console.error("Error getting session:", error);
        setUser(null);
        setSubscriptionData(null);
        setUserOnboarded(null);
        setCachedProfile(null);
        setIdentity(null);
      } finally {
        setLoading(false);
      }
    };

    getSession();
  }, []);

  const signOut = async () => {
    try {
      // Clear all cache before signing out
      if (user?.id) {
        const cacheKey = getOnboardingCacheKey(user.id);
        localStorage.removeItem(cacheKey);
        ProfileCacheService.clearUserCache(user.id);
      }

      await authClient.signOut();
      setUser(null);
      setSubscriptionData(null);
      setUserOnboarded(null);
      setCachedProfile(null);
      setIdentity(null);
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        subscriptionData,
        subscriptionLoading,
        userOnboarded,
        cachedProfile,
        identity,
        identityLoading,
        signOut,
        refreshSubscription,
        refreshOnboardingStatus,
        refreshProfile,
        refreshIdentity,
        invalidateCache,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
